import React, { useCallback, useEffect, useState } from 'react'

// Components
import { AddNewButton } from '../../../components/AddNewButton/AddNewButton'
import { CreatePlayerPlaceholder } from '../../../components/CreatePlayerPlaceholder/CreatePlayerPlaceholder'
import { MainHeader } from '../../../components/MainHeader/MainHeader'
import BasicSelect from '../../../components/Material/Select'
import { PlayerForm } from '../../../metrics_server/players/components/PlayerForm/PlayerForm'
import { SearchableDropdown } from '../../../components/SearchableDropdown/SearchableDropdown'
import { TeamGrid } from '../../../components/TeamGrid/TeamGrid'

// Hooks and utilities
import { useAppDispatch } from '../../../store/hooks'
import { getSport } from '../../../metrics_server/sports/functions'
import { useTeamOptions, useTeams } from '../../../metrics_server/teams/hooks'

// Actions
import { setTeam } from '../../../metrics_server/teams/actions'
import { toggleModal } from '../../../ui/modal/actions'

// Types
import { PlayerData } from '../../../metrics_server/players/types'

// Styles
import styles from '../Teams.module.scss'

export interface TeamsContentProps {
  teamPlayers: PlayerData[]
  setRedirect: (pathname: string) => any
}

export const TeamsContent = ({
  teamPlayers,
  setRedirect
}: TeamsContentProps) => {
  const dispatch = useAppDispatch()
  const teams = useTeams()
  const { teamOptions } = useTeamOptions()
  const { selectedTeam, items } = teams

  const onSelect = (value) => {
    if (value) {
      dispatch(setTeam(value))
    }
  }
  const [players, setPlayers] = useState(teamPlayers)

  const [searchInput, setSearchInput] = useState('')
  const searchPlayers = (input) => setSearchInput(input.toLowerCase())

  const createNewPlayer = useCallback(() => {
    const selectedTeamObj = items[selectedTeam]
    const sport = getSport(selectedTeamObj.sportType)

    dispatch(
      toggleModal({
        active: true,
        type: 'form',
        wrapper: true,
        width: 'auto',
        handleProceed: () => {
          dispatch(toggleModal({}))
        },
        ChildComponent: () => (
          <PlayerForm
            handleProceed={() => {
              dispatch(toggleModal({}))
            }}
            sport={sport}
            teamId={selectedTeam}
          />
        ),
        className: 'modalSmall'
      })
    )
  }, [dispatch, items, selectedTeam])

  useEffect(() => {
    const searchedPlayers = teamPlayers?.filter((player) => {
      const fullName = (
        (player.firstName || '') +
        ' ' +
        (player.lastName || '')
      ).toLowerCase()

      const number = player.number

      if (number === parseInt(searchInput)) {
        return true
      }

      return fullName.includes(searchInput.toLowerCase())
    })

    setPlayers(searchedPlayers)
  }, [searchInput, teamPlayers])

  return (
    <React.Fragment>
      <div className={styles.teamContent}>
        <div className={styles.teamsDropdownContainer}>
          <AddNewButton
            addCallBack={() => setRedirect('/createorganisation')}
            style={{ paddingRight: '20px' }}
            size={45}
          />

          <SearchableDropdown
            items={teamOptions}
            onSelect={onSelect}
            value={teams.selectedTeam}
            label={'Teams'}
            disabledValue={'Second Team'}
            isMultiple={false}
            minWidth={400}
          />
        </div>
      </div>
      <div className={styles.playerContent}>
        {teamPlayers && teamPlayers.length === 0 ? (
          <CreatePlayerPlaceholder createPlayer={createNewPlayer} />
        ) : (
          <>
            <div className={styles.playerSectionHeader}>
              <MainHeader title={'Players'} />
              <div style={{ width: '250px', marginRight: '20px' }}>
                <BasicSelect
                  label={'Search Players'}
                  variant='standard'
                  selected={searchInput}
                  onChange={searchPlayers}
                  labelShrink={true}
                  size={'medium'}
                  fontSize={20}
                />
              </div>
            </div>
            <div className={styles.playersGridContainer}>
              <TeamGrid addPlayer={createNewPlayer} players={players} />
            </div>
          </>
        )}
      </div>
    </React.Fragment>
  )
}
